import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function SecuritySystemsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Security Systems Installation",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Advanced CCTV, access control, and intruder alarm systems designed to protect your premises 24/7 with cutting-edge technology and professional installation.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Security Systems Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "CCTV Installation",
            "description": "High-definition CCTV systems with remote viewing capabilities, night vision, and intelligent motion detection"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Access Control Systems",
            "description": "Sophisticated access control solutions including keycard systems, biometric readers, and keypad entry"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Intruder Alarm Systems",
            "description": "Professional intruder alarm systems with 24/7 monitoring capabilities"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Security Systems North Wales - CCTV Access Control Alarms - LEWIS SECURITY"
        description="Advanced CCTV, access control, and intruder alarm systems designed to protect your premises 24/7 with cutting-edge technology and professional installation in North Wales. NSI Gold approved security systems."
        keywords="CCTV installation North Wales, access control systems, intruder alarms, security systems, 24/7 monitoring, security cameras North Wales"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-red-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Security Systems</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Advanced CCTV, access control, and intruder alarm systems designed to protect your premises 24/7 with cutting-edge technology and professional installation
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Comprehensive Security Protection</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  At LEWIS SECURITY, we provide state-of-the-art security systems tailored to meet the unique needs of businesses and properties across North Wales. Our expert team designs, installs, and maintains security solutions that deliver peace of mind and robust protection against threats.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  With years of experience in the security industry, we understand that every property has different security requirements. Our comprehensive approach ensures that your security system is not only effective but also compliant with all relevant regulations and standards.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/fd2bcf8ce6c29ea8fb1fd3670db9e7d7.jpg"
                  alt="Security Systems Control Room"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Security Solutions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-camera-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">CCTV Installation</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    High-definition CCTV systems with remote viewing capabilities, night vision, and intelligent motion detection. Our cameras provide crystal-clear footage and can be accessed from anywhere via smartphone or computer.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>4K Ultra HD cameras for maximum clarity</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Remote access via mobile app</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Cloud and local storage options</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Advanced analytics and alerts</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-door-lock-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Access Control Systems</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Sophisticated access control solutions including keycard systems, biometric readers, and keypad entry. Control who enters your premises and when, with detailed audit trails and real-time monitoring.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Biometric fingerprint and facial recognition</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Keycard and fob systems</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Time-based access permissions</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Comprehensive access logs</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Intruder Alarm Systems</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Professional intruder alarm systems with 24/7 monitoring capabilities. Our systems detect unauthorized entry and immediately alert you and the authorities, providing rapid response to potential threats.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Wireless and wired alarm options</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Motion sensors and door contacts</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Police-approved monitoring</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Smartphone notifications</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-24-hours-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">24/7 Monitoring Services</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Round-the-clock professional monitoring ensures your property is always protected. Our monitoring center responds immediately to any alerts, coordinating with emergency services when necessary.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>NSI Gold-approved monitoring center</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Immediate response to alerts</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Emergency services coordination</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Regular system health checks</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center font-poppins">Why Choose Our Security Systems?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-award-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Industry Certified</h3>
                  <p className="text-sm text-gray-600 font-inter">NSI Gold and SSAIB approved installations meeting the highest industry standards</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-customer-service-2-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Expert Support</h3>
                  <p className="text-sm text-gray-600 font-inter">Dedicated technical support team available 24/7 for emergencies and assistance</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Proven Reliability</h3>
                  <p className="text-sm text-gray-600 font-inter">Years of experience protecting businesses across North Wales with zero compromises</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Ready to Secure Your Property?</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Contact us today for a free security assessment and discover how our advanced security systems can protect your business
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get Free Quote
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
